import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import dayjs, { Dayjs } from 'dayjs'
import { Form, Formik } from 'formik'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { FormikPartnerSelect } from 'src/components/payments-ops/FormikPartnerSelect'
import { Button } from 'src/components/shared/Button'
import { FormikDatePicker } from 'src/components/shared/form/formik/FormikDatePicker'
import { SectionCard } from 'src/components/shared/SectionCard'
import { AdminRunReconciliation } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { getReconciliationRunning } from 'src/selectors/reconciliation'
import { reconciliationFormValidator } from './ReconciliationForm.validator'

interface ReconciliationFormValues {
  partnerId: string
  startDate: Dayjs | null
}

const INITIAL_VALUES: ReconciliationFormValues = { partnerId: '', startDate: null }

const DATE_FORMAT = 'YYYY-MM-DD'

/** End date is locked to today, as in Retool — the run always reconciles up to now. */
const ReconciliationFormComponent = () => {
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const isRunning = useSelector(getReconciliationRunning)

  const handleSubmit = useCallback(
    async (values: ReconciliationFormValues) => {
      if (!values.startDate) {
        return
      }

      await asyncDispatchWithErrorToast(
        AdminRunReconciliation.request({
          partnerId: values.partnerId,
          startDate: values.startDate.format(DATE_FORMAT),
          endDate: dayjs().format(DATE_FORMAT),
        }),
      )
    },
    [asyncDispatchWithErrorToast],
  )

  return (
    <SectionCard title="Run reconciliation">
      <Formik<ReconciliationFormValues>
        initialValues={INITIAL_VALUES}
        validationSchema={reconciliationFormValidator}
        onSubmit={handleSubmit}
      >
        <Form css={tw`flex flex-row flex-wrap items-end gap-4`}>
          <div css={tw`w-[280px]`}>
            <FormikPartnerSelect<ReconciliationFormValues>
              name="partnerId"
              label="Partner *"
              dataTestId="reconciliation-partner"
              disabled={isRunning}
            />
          </div>
          <div css={tw`w-[200px]`}>
            <FormikDatePicker<ReconciliationFormValues>
              name="startDate"
              label="Start date *"
              maxDate={dayjs()}
              disabled={isRunning}
            />
          </div>
          <div css={tw`flex flex-col pb-2`}>
            <Typography variant="h6" css={tw`text-shades-400`}>
              End date
            </Typography>
            <Typography variant="body2">Today ({dayjs().format('MMM D, YYYY')})</Typography>
          </div>
          <Button
            size="small"
            color="dark"
            type="submit"
            loading={isRunning}
            disabled={isRunning}
            data-testid="reconciliation-run"
          >
            Run reconciliation
          </Button>
        </Form>
      </Formik>
      {isRunning ? (
        <Typography variant="body2" css={tw`mt-3 text-xs text-shades-500`}>
          Pulling US Bank transactions and comparing balances — this can take a while.
        </Typography>
      ) : null}
    </SectionCard>
  )
}

export const ReconciliationForm = memo(ReconciliationFormComponent)
